import { invoke } from '@tauri-apps/api/core';
import {
  FolderInput, RotateCw, FolderPlus, FilePlus, FileArchive, Download, FolderOpen, Pencil, Trash2,
  Shield, FileEdit, CheckSquare, Upload, ExternalLink, FolderSearch, GitCompare,
} from 'lucide-react';
import { useFtpStore } from '../../store/ftpStore';
import { errText } from './ftpViewHelpers';

/** FtpView's context menus (row and blank-space, both panes) and the confirmed delete. */
export function useFtpContextMenus({
  t, remotePath, remoteEntries, remoteEntriesNow, remoteSelection, localEntries, localSelection,
  setNameModal, setEditFile, setPermEntry, confirmDelete, setConfirmDelete, setComparePick,
  setExportSources, activeState, protocol, showNotice, startPathUpload, transferBatch,
  downloadTargets, pickUploadDialog, uploadFolder, openCompress,
}) {
  /* ── Context menus ───────────────────────────────────────────────────── */

  /**
   * The row menu. **It acts on the SELECTION when the clicked row is part of
   * it** — the gap that made multi-select feel unimplemented even though
   * Ctrl-click had always worked: with five files selected, Download and Delete
   * still did one.
   *
   * Clicking a row that is NOT in the selection acts on that row alone
   * (`openMenuAt` has already reduced the selection to it), so the old
   * single-file behaviour is untouched. Rename and Permissions stay
   * single-target whatever is selected: both take one new value, and applying
   * one name to five files is not a thing the user can have meant.
   */
  const buildRemoteMenu = (entry) => {
    const s = useFtpStore.getState();
    const targets =
      remoteSelection.includes(entry.path) && remoteSelection.length > 1
        ? remoteEntries.filter((e) => remoteSelection.includes(e.path))
        : [entry];
    const many = targets.length > 1;
    // The noun stays translated; the count is appended rather than folded into
    // a plural key, so no locale can silently lose it.
    const withCount = (label) => (many ? `${label} (${targets.length})` : label);
    const items = [];
    if (entry.isDir) {
      items.push({
        id: 'open',
        label: t('ftp.open'),
        Icon: FolderOpen,
        onSelect: () => s.listRemote(entry.path).catch(() => {}),
      });
      items.push({
        id: 'download',
        label: withCount(t('ftp.download')),
        Icon: Download,
        onSelect: () => downloadTargets(targets),
      });
    } else {
      items.push({
        id: 'edit',
        label: t('ftp.editFile', 'Edit'),
        Icon: FileEdit,
        onSelect: () => setEditFile(entry),
      });
      items.push({
        id: 'download',
        label: withCount(t('ftp.download')),
        Icon: Download,
        // Folders in the selection are downloaded as folders. This used to skip
        // them, back when folder transfer did not exist.
        onSelect: () => downloadTargets(targets),
      });
    }
    items.push({
      id: 'move',
      label: withCount(t('ftp.moveTo')),
      Icon: FolderInput,
      // Prefilled with where the user IS, so moving up one level is typing
      // `..` and moving somewhere nearby is editing the tail of a path.
      onSelect: () => setNameModal({ mode: 'move', targets, defaultName: remotePath }),
    });
    items.push({
      id: 'rename',
      label: t('ftp.rename'),
      Icon: Pencil,
      onSelect: () => setNameModal({ mode: 'rename', entry }),
    });
    if (protocol === 'sftp') {
      items.push({
        id: 'permissions',
        label: t('ftp.permissions'),
        Icon: Shield,
        onSelect: () => setPermEntry(entry),
      });
    }
    if (protocol === 'sftp') {
      // **SFTP only, and that is not a shortcut.** Archiving happens ON THE
      // SERVER, which needs a shell; a plain FTP control connection has none,
      // so offering this there would be an action that can only fail.
      items.push({
        id: 'compress',
        label: withCount(t('ftp.compress')),
        Icon: FileArchive,
        onSelect: () => {
          openCompress(targets);
        },
      });
    }
    items.push({
      id: 'new-folder',
      label: t('ftp.newFolder'),
      Icon: FolderPlus,
      onSelect: () => setNameModal({ mode: 'mkdir' }),
    });
    items.push({
      id: 'new-file',
      label: t('ftp.newFile'),
      Icon: FilePlus,
      onSelect: () => setNameModal({ mode: 'newfile' }),
    });
    items.push({
      id: 'refresh',
      label: t('ftp.refresh'),
      Icon: RotateCw,
      onSelect: () => s.listRemote(s.getRemotePath()).catch(() => {}),
    });
    items.push({
      id: 'delete',
      label: withCount(t('common.delete')),
      Icon: Trash2,
      danger: true,
      onSelect: () => setConfirmDelete({ entries: targets }),
    });
    return items;
  };

  /**
   * Right-click on blank space in the REMOTE pane. New folder and New file were
   * both reachable before — from a row menu, which requires a row to exist, and
   * from the toolbar — but not from where people look for them, which is the
   * empty space they want to create something in.
   */
  const buildRemoteBackgroundMenu = () => {
    const s = useFtpStore.getState();
    return [
      {
        id: 'new-folder',
        label: t('ftp.newFolder'),
        Icon: FolderPlus,
        onSelect: () => setNameModal({ mode: 'mkdir' }),
      },
      {
        id: 'new-file',
        label: t('ftp.newFile'),
        Icon: FilePlus,
        onSelect: () => setNameModal({ mode: 'newfile' }),
      },
      {
        id: 'upload-here',
        label: t('ftp.uploadHere'),
        Icon: Upload,
        onSelect: pickUploadDialog,
      },
      {
        id: 'select-all',
        label: t('ftp.selectAll'),
        Icon: CheckSquare,
        onSelect: () => s.setRemoteSelection(remoteEntriesNow().map((e) => e.path)),
      },
      {
        id: 'refresh',
        label: t('ftp.refresh'),
        Icon: RotateCw,
        onSelect: () => s.listRemote(s.getRemotePath()).catch(() => {}),
      },
    ];
  };

  /** The local pane's equivalent. It has no create actions — this side is the
   *  user's own disk and the OS file manager owns it — so it offers what it
   *  can actually do. */
  const buildLocalBackgroundMenu = () => {
    const s = useFtpStore.getState();
    return [
      {
        id: 'select-all',
        label: t('ftp.selectAll'),
        Icon: CheckSquare,
        onSelect: () => s.setLocalSelection((s.localEntries || []).map((e) => e.path)),
      },
      {
        id: 'refresh',
        label: t('ftp.refresh'),
        Icon: RotateCw,
        onSelect: () => s.listLocal(s.localPath).catch(() => {}),
      },
    ];
  };

  const buildLocalMenu = (entry) => {
    const s = useFtpStore.getState();
    const items = [];
    // Export takes the whole selection when the clicked row is part of it —
    // the same rule the remote menu follows.
    const localTargets =
      localSelection.includes(entry.path) && localSelection.length > 1
        ? (localEntries || []).filter((e) => localSelection.includes(e.path))
        : [entry];
    if (entry.isDir) {
      items.push({
        id: 'open',
        label: t('ftp.open'),
        Icon: FolderOpen,
        onSelect: () => s.listLocal(entry.path).catch(() => {}),
      });
      if (activeState === 'connected') {
        items.push({
          id: 'upload',
          label: t('ftp.upload'),
          Icon: Upload,
          onSelect: () => uploadFolder(entry.path, entry.name),
        });
      }
    } else {
      // Local files, usable with or without a server connection.
      items.push({
        id: 'edit',
        label: t('ftp.editFile', 'Edit'),
        Icon: FileEdit,
        onSelect: () => setEditFile({ ...entry, local: true }),
      });
      items.push({
        id: 'open-default',
        label: t('ftp.openDefault'),
        Icon: ExternalLink,
        onSelect: () =>
          invoke('ftp_local_open', { path: entry.path }).catch((e) => showNotice(errText(e))),
      });
      if (activeState === 'connected') {
        items.push({
          id: 'compare-remote',
          label: t('ftp.compareWithRemote'),
          Icon: GitCompare,
          onSelect: () => setComparePick(entry),
        });
        items.push({
          id: 'upload',
          label: t('ftp.upload'),
          Icon: Upload,
          onSelect: () =>
            transferBatch('upload', [
              { name: entry.name, start: (meta) => startPathUpload(entry.path, entry.name, undefined, meta) },
            ]),
        });
      }
    }
    items.push({
      id: 'export',
      label:
        localTargets.length > 1
          ? `${t('ftp.exportAs')} (${localTargets.length})`
          : t('ftp.exportAs'),
      Icon: FileArchive,
      onSelect: () => setExportSources(localTargets),
    });
    // Files AND folders: show it in Explorer / Finder, selected, never launched.
    items.push({
      id: 'reveal',
      label: t('ftp.openLocation'),
      Icon: FolderSearch,
      onSelect: () =>
        invoke('ftp_local_reveal', { path: entry.path }).catch((e) => showNotice(errText(e))),
    });
    items.push({
      id: 'refresh',
      label: t('ftp.refresh'),
      Icon: RotateCw,
      onSelect: () => s.listLocal(s.localPath).catch(() => {}),
    });
    return items;
  };

  const doDelete = async () => {
    const targets = confirmDelete?.entries || [];
    setConfirmDelete(null);
    if (targets.length === 0) return;
    // **Sequential, and it does not stop at the first failure.** These are
    // independent deletes; abandoning the rest because one was read-only would
    // leave the user with a half-done job and no idea which half. Every failure
    // is collected and reported once.
    const failed = [];
    for (const entry of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await useFtpStore.getState().deleteRemote(entry.path, entry.isDir);
      } catch (e) {
        failed.push(`${entry.name}: ${errText(e)}`);
      }
    }
    if (failed.length) showNotice(failed.join('; '));
  };

  return {
    buildRemoteMenu, buildRemoteBackgroundMenu, buildLocalBackgroundMenu, buildLocalMenu, doDelete,
  };
}
